<script lang="ts">
  // The top half of an activity's description of itself: name, date, owner, tags and group members,
  // and the terrain profile drawn from its altitude polyline. Paired with ActivityCardDetails, which
  // carries everything from the gauges down; the long card and the details dialog both stack the two.
  import { cn } from '@/lib/utils';
  import TagWrapper from '@/Common/TagWrapper.svelte';
  import CustomActivityTags from '@/Common/CustomActivityTags.svelte';
  import Avatar from '@/Avatar.svelte';
  import GroupMemberAvatars from '@/Common/GroupMemberAvatars.svelte';
  import { time_update_minute } from '@/time';
  import ActivityElevationStrip from './ActivityElevationStrip.svelte';
  import {
    activityCardTags,
    activityDateLabel,
    activityDetailsLink,
    cardLayoutForWidth,
    hasTerrainToShow,
    isOwnActivity,
    type CardLayout,
    type DashboardActivity,
  } from './activityCardUtils';

  export let exercise: DashboardActivity;
  /** Coach/squad dashboards list several athletes, so each card names whose ride it is. */
  export let showOwner: boolean = false;
  /** The dialog draws its own title bar, so it turns this off rather than repeating the name. */
  export let showHeader: boolean = true;
  /**
   * Which tier to lay out for. The long card decides this from the whole card's width, because the
   * image column is part of what it has to fit; anywhere else, leaving it unset measures this
   * column itself.
   */
  export let layout: CardLayout | null = null;
  /**
   * Set by the `md` card, the one place this sits beside the image rather than above the details.
   * It holds the column to the image's height and pushes the terrain profile to the bottom of it, so
   * the profile and the picture end on the same line instead of the profile floating up the middle
   * of the grey. The gap that opens up lands between the tags and the profile.
   */
  export let besideImage: boolean = false;

  let ownSize: ResizeObserverSize[] | null = null;
  let ownWidth: number = 0;
  $: if (ownSize && ownSize[0]) ownWidth = ownSize[0].inlineSize;
  $: tier = layout ?? cardLayoutForWidth(ownWidth);

  $: tags = activityCardTags(exercise, 'medium');
  $: dateLabel = activityDateLabel(exercise, $time_update_minute);
  $: detailsLink = activityDetailsLink(exercise);
  $: owner = exercise?.user;
  $: groupMembers = exercise?.group_members ?? [];
  /**
   * Wide enough to split the top of the card in two: title, date and tags on the left, the terrain
   * profile on the right. Below that the profile needs the full width for its three numbers, so it
   * drops to a row of its own under the tags.
   */
  $: splitTop = tier === 'lg' && hasTerrain;
  /**
   * Nothing to draw or count: a trainer ride reports no altitude, or the same altitude all the way
   * through, and elevation gain and KGE are both statements about terrain it never had. Those two
   * go with the profile; distance is a fact about any ride, so ActivityCardDetails falls back to
   * showing it in the metrics row.
   */
  $: hasTerrain = hasTerrainToShow(exercise);
</script>

<div
  bind:borderBoxSize={ownSize}
  class={cn(
    'min-w-0',
    splitTop ? 'flex flex-row items-center gap-6' : 'flex flex-col gap-2.5',
    // 150px is the height ActivityCardImages gives the `md` frame.
    besideImage ? 'min-h-[150px] justify-between' : ''
  )}
>
  <div class={cn('min-w-0 flex flex-col gap-2.5', splitTop ? 'flex-1 basis-1/2' : '')}>
    {#if showHeader}
      <div class={cn('min-w-0', tier === 'xs' ? '' : 'flex flex-row items-start justify-between gap-3')}>
        <div class="min-w-0">
          <h3 class="!whitespace-normal w-full font-medium m-0 text-base line-clamp-2">
            <a class="text-foreground hover:underline" href={detailsLink} target="activity" on:click={(e) => e.stopPropagation()}>
              {exercise.name}
            </a>
          </h3>
          <div class="text-xs text-muted-foreground">{dateLabel}</div>
        </div>
        {#if showOwner && owner?.username}
          <div class={cn('flex flex-row items-center gap-2 min-w-0', tier === 'xs' ? 'mt-1.5' : 'shrink-0')}>
            <Avatar
              shape="circle"
              pad_right={false}
              username={owner.username}
              first_name={owner.first_name ?? ''}
              last_name={owner.last_name ?? ''}
              profile_photo={owner.profile_photo ?? ''}
              --avatar-height="20px"
            />
            <span class="text-xs text-muted-foreground truncate">{owner.first_name} {owner.last_name}</span>
          </div>
        {/if}
      </div>
    {/if}

    <div class="flex flex-row flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
      <div class='flex flex-row flex-wrap items-center gap-1'>
        <!-- `contents` so the automatic tags flow into this row beside the custom ones; TagWrapper is
             what turns the Group tag into its members popover. -->
        <TagWrapper class="contents" {tags} {groupMembers} />
        <CustomActivityTags {exercise} size="medium" readonly={!isOwnActivity(exercise)} />
      </div>
      {#if groupMembers.length}
        <GroupMemberAvatars members={groupMembers} size={22} />
      {/if}
    </div>
  </div>

  {#if hasTerrain}
    <!-- Terrain: the altitude profile with distance, elevation and KGE read off it. -->
    <div class={cn('min-w-0', splitTop ? 'flex-1 basis-1/2' : '')}>
      <ActivityElevationStrip {exercise} height={splitTop ? 70 : 52} />
    </div>
  {/if}
</div>
